/**
 * Photos as Polaroids: a white instant-print frame with the deep bottom edge,
 * a soft sheen, a small steady tilt, and a handwritten line underneath.
 *
 *  - `Polaroid` — one print. Tapping it opens the viewer.
 *  - `PolaroidStack` — a letter's photos, dropped loosely on the page.
 *  - `PhotoViewer` — one print, full size and uncropped, with ‹ › between them.
 *    The sender can take a photo back from here, and caption it while the
 *    letter is still sealed.
 *
 * The handwritten line on a print is the sender's caption when there is one,
 * otherwise the line the caller passes (when, and from whom).
 *
 * A photo that has arrived is the reader's to keep, whatever happens to the
 * pair's trust. One the sender took back (or that will not load) shows as an
 * undeveloped print with a quiet line, never a broken image.
 */

import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { MAX_CAPTION_CHARS, useCaptionPhotoMutation, useTakeBackPhotoMutation } from '../letters/api';
import { printShape, tiltFor, type LetterPhoto } from './scrapbook';

export function Polaroid({
  photo,
  caption,
  tilt = tiltFor(photo.id),
  blankNote,
  size = 'medium',
  tape = false,
  onOpen,
}: {
  photo: LetterPhoto;
  caption?: ReactNode;
  tilt?: number;
  /** What an undeveloped print says, e.g. "@leon took this photo back." */
  blankNote?: string;
  size?: 'small' | 'medium' | 'large';
  /** A strip of tape across the top, for the scrapbook. */
  tape?: boolean;
  onOpen?: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const blank = photo.takenBack || failed;
  const shape = printShape(photo.width, photo.height);
  const cls = `polaroid polaroid--${size} polaroid--${shape}${blank ? ' polaroid--blank' : ''}${tape ? ' polaroid--taped' : ''}`;
  const style = { '--tilt': `${tilt}deg` } as React.CSSProperties;
  const note = photo.takenBack ? (blankNote ?? 'This photo was taken back.') : 'This photo can’t be shown just now.';
  const line = (!blank && photo.caption) || caption;
  const inner = (
    <>
      <span className="polaroid__window">
        {blank ? (
          <span className="polaroid__blank">{note}</span>
        ) : (
          <img
            src={photo.url}
            alt={photo.caption ?? ''}
            loading="lazy"
            width={photo.width ?? undefined}
            height={photo.height ?? undefined}
            onError={() => setFailed(true)}
          />
        )}
      </span>
      {line && <span className="polaroid__caption">{line}</span>}
    </>
  );
  if (blank || !onOpen) {
    return (
      <span className={cls} style={style} role="img" aria-label={blank ? note : 'Photo'}>
        {inner}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      style={style}
      onClick={onOpen}
      aria-label={photo.caption ? `See this photo full size: ${photo.caption}` : 'See this photo full size'}
    >
      {inner}
    </button>
  );
}

/** A letter's photos, dropped on the page a little overlapped, at the foot of the letter. */
export function PolaroidStack({
  photos,
  caption,
  mine,
  captionable = false,
  peerName,
}: {
  photos: LetterPhoto[];
  caption?: string;
  /** I sent them: I may take them back. */
  mine: boolean;
  /** Mine, and the letter is still sealed: I may caption them. */
  captionable?: boolean;
  peerName: string;
}) {
  const [open, setOpen] = useState<number | null>(null);
  if (photos.length === 0) return null;
  const kept = photos.filter((p) => !p.takenBack);
  return (
    <div
      className={`polaroid-stack${photos.length > 1 ? ' polaroid-stack--many' : ''}`}
      aria-label={photos.length === 1 ? 'A photo with this letter' : `${photos.length} photos with this letter`}
    >
      {photos.map((p) => (
        <Polaroid
          key={p.id}
          photo={p}
          caption={caption}
          blankNote={mine ? 'You took this photo back.' : `@${peerName} took this photo back.`}
          onOpen={() => setOpen(kept.indexOf(p))}
        />
      ))}
      {open != null && kept[open] && (
        <PhotoViewer
          prints={kept.map((p) => ({ photo: p, caption, canTakeBack: mine, canCaption: mine && captionable }))}
          peerName={peerName}
          index={open}
          onIndex={setOpen}
          onClose={() => setOpen(null)}
        />
      )}
    </div>
  );
}

export interface ViewerPrint {
  photo: LetterPhoto;
  caption?: string;
  /** Shown as a link under the print, e.g. "Read the letter". */
  action?: { label: string; onClick: () => void };
  /** I sent it, so I may take it back. */
  canTakeBack?: boolean;
  /** I sent it and its letter is still sealed, so I may caption it. */
  canCaption?: boolean;
}

/** One print at a time, as large as the screen allows, the whole photo uncropped. */
export function PhotoViewer({
  prints,
  peerName,
  index,
  onIndex,
  onClose,
}: {
  prints: ViewerPrint[];
  peerName: string;
  index: number;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const n = prints.length;
  const [confirming, setConfirming] = useState(false);
  const [captioning, setCaptioning] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [takeBack, { isLoading: taking }] = useTakeBackPhotoMutation();
  const [saveCaption, { isLoading: saving }] = useCaptionPhotoMutation();
  const step = useCallback(
    (d: number) => {
      setConfirming(false);
      setCaptioning(false);
      setError(null);
      onIndex((index + d + n) % n);
    },
    [index, n, onIndex],
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && n > 1) step(1);
      else if (e.key === 'ArrowLeft' && n > 1) step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step, n]);

  const print = prints[index];
  if (!print) return null;
  const { photo } = print;

  const startCaption = () => {
    setError(null);
    setDraft(photo.caption ?? '');
    setCaptioning(true);
  };

  const onSaveCaption = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await saveCaption({ id: photo.id, caption: draft.trim() || null }).unwrap();
      setCaptioning(false);
    } catch (err) {
      const msg = (err as { data?: { message?: string } })?.data?.message;
      setError(msg ?? 'Couldn’t save the caption just now. Try again in a moment.');
    }
  };

  const onTakeBack = async () => {
    setError(null);
    try {
      await takeBack({ id: photo.id }).unwrap();
      // The print leaves the list on refetch; close rather than jump to another.
      onClose();
    } catch {
      setError('Couldn’t take it back just now. Try again in a moment.');
    }
  };

  return createPortal(
    <div className="photo-viewer" role="dialog" aria-modal="true" aria-label="Photo" onClick={onClose}>
      <button type="button" className="photo-viewer__close" onClick={onClose} aria-label="Close">
        <X size={20} />
      </button>
      {n > 1 && (
        <button
          type="button"
          className="photo-viewer__step photo-viewer__step--prev"
          onClick={(e) => {
            e.stopPropagation();
            step(-1);
          }}
          aria-label="Previous photo"
        >
          <ChevronLeft size={24} />
        </button>
      )}
      <figure
        key={photo.id}
        className="photo-viewer__print"
        style={{ '--tilt': `${tiltFor(photo.id, 1.5)}deg` } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.url}
          alt={photo.caption ?? 'Photo sent with a letter'}
          width={photo.width ?? undefined}
          height={photo.height ?? undefined}
        />
        <figcaption className="photo-viewer__caption">
          {photo.caption && <span className="photo-viewer__words">{photo.caption}</span>}
          {print.caption && <span className={photo.caption ? 'photo-viewer__meta' : undefined}>{print.caption}</span>}
          {n > 1 && (
            <span className="photo-viewer__count">
              {index + 1} of {n}
            </span>
          )}
        </figcaption>
        {captioning ? (
          <form className="photo-viewer__caption-form" onSubmit={(e) => void onSaveCaption(e)}>
            <label htmlFor={`caption-${photo.id}`} className="visually-hidden">
              Caption
            </label>
            <input
              id={`caption-${photo.id}`}
              className="field__input"
              value={draft}
              maxLength={MAX_CAPTION_CHARS}
              placeholder="Write a line under the photo"
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
            />
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <div className="photo-viewer__confirm-actions">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save caption'}
              </button>
              <button type="button" className="btn btn--ghost" disabled={saving} onClick={() => setCaptioning(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : confirming ? (
          <div className="photo-viewer__confirm" role="group" aria-label="Take this photo back">
            <p>Take it back? It’s deleted, and @{peerName} won’t be able to see it any more.</p>
            {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <div className="photo-viewer__confirm-actions">
              <button type="button" className="btn btn--danger" disabled={taking} onClick={() => void onTakeBack()}>
                {taking ? 'Taking it back…' : 'Take it back'}
              </button>
              <button type="button" className="btn btn--ghost" disabled={taking} onClick={() => setConfirming(false)}>
                Keep it
              </button>
            </div>
          </div>
        ) : (
          (print.action || print.canTakeBack || print.canCaption) && (
            <div className="photo-viewer__actions">
              {print.action && (
                <button type="button" className="linkish" onClick={print.action.onClick}>
                  {print.action.label}
                </button>
              )}
              {print.canCaption && (
                <button type="button" className="linkish" onClick={startCaption}>
                  {photo.caption ? 'Edit the caption' : 'Add a caption'}
                </button>
              )}
              {print.canTakeBack && (
                <button type="button" className="linkish photo-viewer__take-back" onClick={() => setConfirming(true)}>
                  Take this photo back
                </button>
              )}
            </div>
          )
        )}
      </figure>
      {n > 1 && (
        <button
          type="button"
          className="photo-viewer__step photo-viewer__step--next"
          onClick={(e) => {
            e.stopPropagation();
            step(1);
          }}
          aria-label="Next photo"
        >
          <ChevronRight size={24} />
        </button>
      )}
    </div>,
    document.body,
  );
}
